// /state/teamStore.js
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

// Prevent race conditions: one in-flight request per team_id
const __teamMemberIdsInflight = {};
const TEAM_MEMBER_IDS_TTL_MS = 2 * 60 * 1000; // 2 minutes

const useTeamStore = create(
  persist(
    (set, get) => ({
      // ---- persisted state ----
      myTeams: [],                 // array of team objects the user belongs to
      activeTeamId: null,          // single source of truth for the currently active team
      selectedTeam: null,          // (optional) specific team being viewed
      teamInvites: [],
      teamApplications: [],
      isCaptain: false,

      // Team membership cache (team_id -> [u_id, ...])
      teamMemberIdsByTeamId: {},
      teamMemberIdsFetchedAtByTeamId: {},

      // Captain-side invite draft (persisted)
      // { invitee_u_id, team_id, created_at, status }
      inviteDraft: null,

      // --- Local teams (Evaluate Teams) -----------------------------
      nearbyTeamsLocal: [],
      teamGeoOverrideLocal: [],
      lastLocalCenter: null,
      lastLocalRadius: 100,
      updatedAtLocal: 0,

      // ---- setters ----
      setMyTeams: (next) =>
        set((s) => {
          const val = typeof next === 'function' ? next(s.myTeams) : next;
          return { myTeams: Array.isArray(val) ? val : [] };
        }),

      // Canonical server hydration for "my teams"
      // - Uses the known-good endpoint used elsewhere in the app
      // - Delegates normalization + isCaptain computation to hydrateTeams()
      ensureMyTeamsHydrated: async (viewerUId) => {
        const uid = Number(viewerUId || 0);
        if (!uid) return { ok: false, error: 'no_uid' };

        const s = get();

        // If teams are already in memory, do not refetch.
        if (Array.isArray(s.myTeams) && s.myTeams.length > 0) {
          return { ok: true, skipped: 'already_in_memory', teams: s.myTeams };
        }

        try {
          const res = await fetch(`/api/teams/recruiting/debug/my-teams?u_id=${uid}`, {
            credentials: 'include',
          });

          const json = await res.json().catch(() => ({}));
          const teams = Array.isArray(json?.teams) ? json.teams : [];

          // Reuse your existing canonical normalization + captain logic
          get().hydrateTeams(teams);

          return { ok: true, teams: get().myTeams };
        } catch (e) {
          return { ok: false, error: String(e?.message || e || 'hydrate_failed') };
        }
      },

      // Canonical server hydration for team member u_ids (per team)
      // - DB is source of truth
      // - Dedupe in-flight requests per team_id to prevent race conditions
      // - TTL-based cache with optional force refresh
      ensureTeamMemberIds: async (teamId, opts = {}) => {
        const tId = Number(teamId || 0);
        if (!tId) return { ok: false, error: 'no_team_id' };

        const TEAM_MEMBER_IDS_TTL_MS = 2 * 60 * 1000; // 2 minutes
        const force = opts?.force === true;

        const s = get();
        const cachedIds = s.teamMemberIdsByTeamId?.[tId];
        const cachedAt = s.teamMemberIdsFetchedAtByTeamId?.[tId] || 0;

        const isFresh =
          Array.isArray(cachedIds) &&
          (Date.now() - cachedAt) < TEAM_MEMBER_IDS_TTL_MS;

        if (!force && isFresh) {
          return { ok: true, team_id: tId, u_ids: cachedIds, skipped: 'fresh_cache' };
        }

        // One in-flight request per team_id (prevents race conditions)
        if (!window.__teamMemberIdsInflight) window.__teamMemberIdsInflight = {};
        if (window.__teamMemberIdsInflight[tId]) {
          return window.__teamMemberIdsInflight[tId];
        }

        window.__teamMemberIdsInflight[tId] = (async () => {
          try {
            const res = await fetch(`/api/teams/members/ids?team_id=${tId}`, {
              credentials: 'include',
            });

            const json = await res.json().catch(() => ({}));
            if (!res.ok || !json?.ok) {
              return { ok: false, error: json?.error || `HTTP ${res.status}` };
            }

            const ids = Array.isArray(json?.u_ids)
              ? json.u_ids.map((n) => Number(n)).filter((n) => Number.isFinite(n))
              : [];

            set((state) => ({
              teamMemberIdsByTeamId: {
                ...(state.teamMemberIdsByTeamId || {}),
                [tId]: ids,
              },
              teamMemberIdsFetchedAtByTeamId: {
                ...(state.teamMemberIdsFetchedAtByTeamId || {}),
                [tId]: Date.now(),
              },
            }));

            return { ok: true, team_id: tId, u_ids: ids };
          } catch (e) {
            return { ok: false, error: String(e?.message || e || 'hydrate_failed') };
          } finally {
            if (window.__teamMemberIdsInflight) {
              delete window.__teamMemberIdsInflight[tId];
            }
          }
        })();

        return window.__teamMemberIdsInflight[tId];
      },

      setActiveTeamId: (id) => {
        const teamId = Number(id);
        set({ activeTeamId: Number.isFinite(teamId) ? teamId : null });
      },

      setSelectedTeam: (team) => set({ selectedTeam: team || null }),
      setTeamInvites: (invites) => set({ teamInvites: Array.isArray(invites) ? invites : [] }),
      setTeamApplications: (apps) => set({ teamApplications: Array.isArray(apps) ? apps : [] }),
      setIsCaptain: (value) => set({ isCaptain: !!value }),

      // ---- invite draft helpers (captain-side) ----
      setInviteDraft: (draft) => set({ inviteDraft: draft || null }),
      clearInviteDraft: () => set({ inviteDraft: null }),
      getInviteDraft: () => get().inviteDraft,


  // Improved hydrateTeams that maintains chronological order
  hydrateTeams: (teams) => {
    const list = Array.isArray(teams) ? teams : [];

    // --- Read signed-in profile from the persisted Profile store (eh_profile_v1)
    let viewer = null;
    try {
      const raw = localStorage.getItem('eh_profile_v1');
      if (raw) {
        const parsed = JSON.parse(raw);
        // common shapes: {state:{profile:{...}}} or {profile:{...}}
        viewer = parsed?.state?.profile || parsed?.profile || null;
      }
    } catch (_) { viewer = null; }

    // Normalize teams and enrich with owner_* (if owner is the viewer)
    const norm = list.map(t => {
      const base = {
        ...t,
        is_active: !!(t && (t.is_active === true || t.is_active === 1 || t.is_active === '1' || t.is_active === 'true' || t.is_active === 't')),
        team_id: Number(t?.team_id),
        created_at: t?.created_at || t?.joined_at || new Date().toISOString(),
        joined_at: t?.joined_at || t?.created_at || new Date().toISOString()
      };

      // If the signed-in user owns this team, stamp their name/photo on the team object
      if (viewer && String(base?.owner_u_id ?? base?.ownerId ?? base?.captain_u_id) === String(viewer?.u_id)) {
        const first =
          viewer?.u_first_name || viewer?.first_name || viewer?.given_name || '';
        const last  =
          viewer?.u_last_name  || viewer?.last_name  || viewer?.family_name || '';
        const pic   =
          viewer?.u_profile_picture || viewer?.profile_picture || viewer?.photo_url || viewer?.avatar_url || '';

        base.owner_first_name = base.owner_first_name || first;
        base.owner_last_name  = base.owner_last_name  || last;
        base.owner_profile_picture = base.owner_profile_picture || pic;
      }

      return base;
    });

    // Sort by date (oldest first)
    norm.sort((a, b) => new Date(a.joined_at || a.created_at) - new Date(b.joined_at || b.created_at));

    // Prefer persisted activeTeamId, else any active, else first
    const savedId = Number(get().activeTeamId);
    const bySaved = Number.isFinite(savedId) ? norm.find(t => t.team_id === savedId) : null;
    const anyActive = norm.find(t => t.is_active);
    const chosen = bySaved || anyActive || norm[0] || null;

    set({
      myTeams: norm,
      activeTeamId: chosen ? chosen.team_id : null,

      // Captain if:
      // - role is 'captain', OR
      // - viewer owns the team (owner_u_id / captain_u_id match)
      isCaptain: norm.some((t) => {
        const role = String(t?.role || '').toLowerCase();
        if (role === 'captain') return true;

        const ownerId = t?.owner_u_id ?? t?.ownerId ?? t?.captain_u_id;
        return viewer && String(ownerId) === String(viewer?.u_id);
      }),
    });

  },


      // Helper to update a single team without replacing entire list
      updateTeam: (teamId, updates) => {
        set((s) => ({
          myTeams: s.myTeams.map(t => 
            Number(t.team_id) === Number(teamId) ? { ...t, ...updates } : t
          )
        }));
      },

      // Helper to remove a single team
      removeTeam: (teamId) => {
        set((s) => ({
          myTeams: s.myTeams.filter(t => Number(t.team_id) !== Number(teamId)),
          // Reset activeTeamId if we're removing the active team
          activeTeamId: s.activeTeamId === Number(teamId) ? null : s.activeTeamId
        }));
      },

      // ---- Local teams actions ----
      setNearbyTeamsLocal: (teams, meta = {}) => {
        const arr = Array.isArray(teams) ? teams : [];
        const center = meta.center ?? get().lastLocalCenter;
        const radius = Number.isFinite(meta.radius) ? meta.radius : get().lastLocalRadius;
        set({
          nearbyTeamsLocal: arr,
          lastLocalCenter: center,
          lastLocalRadius: radius,
          updatedAtLocal: Date.now(),
        });
      },

      setTeamGeoOverrideLocal: (mapOrArray) => {
        let arr = [];
        if (mapOrArray instanceof Map) {
          arr = Array.from(mapOrArray.entries());
        } else if (Array.isArray(mapOrArray)) {
          arr = mapOrArray;
        }
        set({
          teamGeoOverrideLocal: arr,
          updatedAtLocal: Date.now(),
        });
      },

        // Add this to your teamStore actions:
        createTeam: async (teamData) => {
          const res = await fetch('/api/teams/recruiting/team/create', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(teamData)
          });
          const json = await res.json();
          
          if (!res.ok || !json.ok) {
            throw new Error(json.error || 'creation_failed');
          }
          
          // Add to store
          set((s) => ({
            myTeams: [...s.myTeams, { 
              ...json.team, 
              role: 'captain',
              joined_at: new Date().toISOString(),
              created_at: new Date().toISOString()
            }]
          }));
          
          return json.team;
        },

      // ---- reset ----
      resetTeamState: () => set({
        myTeams: [],
        activeTeamId: null,
        selectedTeam: null,
        teamInvites: [],
        teamApplications: [],
        isCaptain: false,

        teamMemberIdsByTeamId: {},
        teamMemberIdsFetchedAtByTeamId: {},

        nearbyTeamsLocal: [],

        teamGeoOverrideLocal: [],
        lastLocalCenter: null,
        lastLocalRadius: 100,
        updatedAtLocal: 0,
      }),
    }),
    {
      name: 'eh_team_v1',
      storage: createJSONStorage(() => localStorage),
      version: 2,
      migrate: (state, fromVersion) => {
        if (fromVersion < 2) {
          return {
            ...state,
            nearbyTeamsLocal: [],
            teamGeoOverrideLocal: [],
            lastLocalCenter: null,
            lastLocalRadius: 100,
            updatedAtLocal: 0,
          };
        }
        return state;
      },
      partialize: (s) => ({
        myTeams: s.myTeams,
        activeTeamId: s.activeTeamId,
        selectedTeam: s.selectedTeam,
        teamInvites: s.teamInvites,
        teamApplications: s.teamApplications,
        isCaptain: s.isCaptain,
        inviteDraft: s.inviteDraft,
        nearbyTeamsLocal: s.nearbyTeamsLocal,
        teamGeoOverrideLocal: s.teamGeoOverrideLocal,
        lastLocalCenter: s.lastLocalCenter,
        lastLocalRadius: s.lastLocalRadius,
        updatedAtLocal: s.updatedAtLocal,

        teamMemberIdsByTeamId: s.teamMemberIdsByTeamId,
        teamMemberIdsFetchedAtByTeamId: s.teamMemberIdsFetchedAtByTeamId,

      }),
    }
  )
);

export default useTeamStore;
if (typeof window !== 'undefined') { window.__teamStore = useTeamStore; }
